import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserContext } from './UserContextBase';
import type { PortalSessionGetResponse, VerifiedPerson } from '../hooks/useSessionPolling';
import { ensureUserIdCookie, getOrCreateUserId } from '../utils/userIdCookie';

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<VerifiedPerson | null>(null);
  const [credentialId, setCredentialIdState] = useState<string | null>(null);

  useEffect(() => {
    try {
      const id = getOrCreateUserId();
      setUserId(id);
    } catch (e) {
      console.error('Failed to init userId', e);
    }
  }, []);

  const syncFromSessionResponse = useCallback((json: PortalSessionGetResponse) => {
    if (json.ok && json.data) {
      setSessionData(json.data as VerifiedPerson);
    } else {
      setSessionData(null);
    }
    setCredentialIdState(json.studentIdCrsCredentialId ?? null);
  }, []);

  const refreshPortalSession = useCallback(async () => {
    try {
      ensureUserIdCookie();
      const resp = await fetch('/api/session', { method: 'GET', credentials: 'include' });
      if (!resp.ok) return;
      const json = (await resp.json()) as PortalSessionGetResponse;
      syncFromSessionResponse(json);
    } catch {
      /* ignore */
    }
  }, [syncFromSessionResponse]);

  useEffect(() => {
    if (!userId) return;
    void refreshPortalSession();
  }, [userId, refreshPortalSession]);

  const value = useMemo(
    () => ({
      userId,
      sessionData,
      setSessionData,
      credentialId,
      syncFromSessionResponse,
      refreshPortalSession,
    }),
    [userId, sessionData, credentialId, syncFromSessionResponse, refreshPortalSession],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
